export type EmployeeStatus = 'active' | 'inactive';

/**
 * 'not-applicable' covers non-educator roles (cuisinière, direction), where the
 * MFA payroll sheet leaves the Q/NQ column blank. Ratio math counts only
 * 'qualified', so these staff correctly never satisfy a qualified-ratio rule.
 */
export type QualificationStatus = 'qualified' | 'non-qualified' | 'not-applicable';

export type TimeOffType = 'vacation' | 'sick' | 'day-off' | 'emergency' | 'appointment';

export type ComplianceDocStatus = 'missing' | 'valid' | 'expiring';

export type EmployeeDocType = 'cv' | 'police-check' | 'first-aid' | 'payslip';

/**
 * One entry per document *type* — the compliance summary the overview tables
 * and alert engine read. Derived on the server from the uploaded files
 * (`EmployeeDocumentFile`), so it is never edited directly.
 */
export interface EmployeeDocumentRecord {
  type: EmployeeDocType;
  status: ComplianceDocStatus;
  /** Start of validity window — unset for CV / payslip (N/A). */
  validFrom?: string;
  expiryDate?: string;
  /** File name of the current upload for this type, if any. */
  fileLabel?: string;
  notes?: string;
}

/**
 * One uploaded file. `id` is the Convex row id, `storageId` the file blob id,
 * `employeeId` the owner, and `createdAt` when it was uploaded. `url` is a
 * short-lived signed download link minted per read.
 */
export interface EmployeeDocumentFile {
  id: string;
  employeeId: string;
  storageId: string;
  type: EmployeeDocType;
  fileName: string;
  mimeType?: string;
  sizeBytes?: number;
  status: 'active' | 'expired';
  validFrom?: string;
  expiryDate?: string;
  createdAt: string;
  notes?: string;
  url: string | null;
}

export const EMPLOYEE_DOC_TYPES: EmployeeDocType[] = [
  'cv',
  'police-check',
  'first-aid',
  'payslip',
];

export interface EmployeeMfaProfile {
  qualification: QualificationStatus;
  /** MFA mapping code — not a payroll calculation. */
  echelonCode: string | null;
  /** Hours transferred from prior employers. */
  ancienneteHours: number;
  /** Updated from validated timesheets. */
  accumulatedHours: number;
  /** Display-only base rate for B1.2 overview. */
  baseRateDisplay: number | null;
}

export interface EmployeeComplianceFlags {
  policeCheck: ComplianceDocStatus;
  firstAid: ComplianceDocStatus;
  firstAidExpiryDate?: string;
}

export interface EmployeePortfolio {
  tags: string[];
  managerNotes: string;
  /** 1–5 management rating, or null if unset. */
  rating: number | null;
  /** Qualitative observation notes (Portfolio column). */
  observation: string;
  /** Evaluation notes (Portfolio column). */
  evaluation: string;
  /** Notices / avis (Portfolio column). */
  notice: string;
}

/** Banking details from an employee void cheque (CPA format) for payroll PAD. */
export interface EmployeeVoidCheque {
  accountHolderName: string;
  bankName?: string;
  /** 3-digit Canadian institution number */
  institutionNumber: string;
  /** 5-digit transit / branch number */
  transitNumber: string;
  accountNumber: string;
  notes?: string;
  recordedAt: string;
  recordedBy?: string;
  updatedAt: string;
}

export interface Employee {
  id: string;
  /**
   * Optional pre-Convex localStorage id (e.g. `teacher-1`). Used to bridge
   * punches / published shifts / time-off still keyed by the old id.
   */
  legacyId?: string;
  firstName: string;
  lastName: string;
  email?: string;
  status: EmployeeStatus;
  /** Hashed PIN — never store plain text. */
  pinHash: string;
  hireDate?: string;
  /** Multi-tenant scope — defaults to active centre on create. */
  centreId?: string;
  /**
   * MFA salary-grid job role (e.g. "Éducatrice qualifiée", "Adjointe administrative").
   * Used with échelon for Role-Scale display in Remuneration.
   */
  jobRole?: string;
  mfa: EmployeeMfaProfile;
  compliance: EmployeeComplianceFlags;
  portfolio: EmployeePortfolio;
  documents: EmployeeDocumentRecord[];
  voidCheque?: EmployeeVoidCheque;
  /** Manager-set: portal invitation has been sent for this employee. */
  inviteSent?: boolean;
  /** Set when this HR row is linked to a Clerk user. */
  clerkUserId?: string;
}

export const ECHELON_REVIEW_THRESHOLD_HOURS = 1664;

export function createDefaultMfaProfile(
  overrides: Partial<EmployeeMfaProfile> = {}
): EmployeeMfaProfile {
  return {
    qualification: 'qualified',
    echelonCode: null,
    ancienneteHours: 0,
    accumulatedHours: 0,
    baseRateDisplay: null,
    ...overrides,
  };
}

export function createDefaultCompliance(
  overrides: Partial<EmployeeComplianceFlags> = {}
): EmployeeComplianceFlags {
  return {
    policeCheck: 'missing',
    firstAid: 'missing',
    ...overrides,
  };
}

export function createDefaultPortfolio(
  overrides: Partial<EmployeePortfolio> = {}
): EmployeePortfolio {
  return {
    tags: [],
    managerNotes: '',
    rating: null,
    observation: '',
    evaluation: '',
    notice: '',
    ...overrides,
  };
}

export function createDefaultDocuments(
  compliance: EmployeeComplianceFlags = createDefaultCompliance()
): EmployeeDocumentRecord[] {
  return [
    { type: 'cv', status: 'missing' },
    { type: 'police-check', status: compliance.policeCheck },
    { type: 'first-aid', status: compliance.firstAid, expiryDate: compliance.firstAidExpiryDate },
    { type: 'payslip', status: 'missing' },
  ];
}

export function syncComplianceFromDocuments(
  documents: EmployeeDocumentRecord[]
): EmployeeComplianceFlags {
  const police = documents.find((d) => d.type === 'police-check');
  const firstAid = documents.find((d) => d.type === 'first-aid');
  return {
    policeCheck: police?.status ?? 'missing',
    firstAid: firstAid?.status ?? 'missing',
    firstAidExpiryDate: firstAid?.expiryDate,
  };
}

/** Normal clock actions at the office kiosk. */
export type PunchActionType = 'in' | 'out' | 'lunch-start' | 'lunch-end';

export type PunchEventType = PunchActionType | 'void';

export type PunchSource = 'office';

export type PunchCorrectionReason =
  | 'accidental-tap'
  | 'wrong-type'
  | 'forgot-punch'
  | 'other';

/** Required when punching out before the assigned shift end. */
export type EarlyOutReason = 'maladie' | 'emergency' | 'no_more_kids';

export const EARLY_OUT_REASONS: readonly EarlyOutReason[] = [
  'maladie',
  'emergency',
  'no_more_kids',
] as const;

export type WorkState = 'none' | 'working' | 'on_lunch' | 'done';

export interface PunchEvent {
  id: string;
  employeeId: string;
  type: PunchEventType;
  at: string;
  source: PunchSource;
  /** When type is void — id of the event being voided. */
  voidsEventId?: string;
  reason?: PunchCorrectionReason;
  reasonNote?: string;
  /** Set on early punch-out before assigned shift end. */
  earlyOutReason?: EarlyOutReason;
  /** Office staff present who approved a correction. */
  approvedByEmployeeId?: string;
}

export type EmployeeSessionChannel = 'office' | 'employee';

export interface EmployeeSession {
  employeeId: string;
  channel: EmployeeSessionChannel;
  startedAt: string;
}

export interface TodayPunchStatus {
  clockIn: string | null;
  clockOut: string | null;
  lunchStart: string | null;
  lunchEnd: string | null;
  workState: WorkState;
  /** @deprecated use workState === 'working' || workState === 'on_lunch' */
  isCurrentlyIn: boolean;
}

export interface DayPunchRecord {
  dateKey: string;
  clockIn: string | null;
  clockOut: string | null;
  lunchStart: string | null;
  lunchEnd: string | null;
  events: PunchEvent[];
}

/** @deprecated use PunchActionType */
export type PunchType = PunchActionType;
